// Announcements to firm users in every cell: write one, choose how it arrives and who gets it, schedule, send.
// A pop-up to every firm waits for a second platform admin.
import { useCallback, useEffect, useState } from 'react';
import { Eye, Megaphone, Send } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Banner, Button, Card, Pill, Select, Spinner, TextArea, TextField, cx, type PillTone } from '../../ui';
import { useMe } from '../AdminShell';
import { AnnouncementPreview, type PreviewAnnouncement } from './AnnouncementPreview';

type Delivery = 'inbox' | 'banner' | 'popup';
type Status = 'draft' | 'pending_confirmation' | 'published' | 'archived';
type Announcement = {
  id: string; title: string; body: string; link_path: string | null; delivery: Delivery; tone: 'info' | 'success' | 'warning';
  audience_firms: string[] | null; audience_role: 'everyone' | 'firm_admins'; release_id: string | null; release_name: string | null;
  status: Status; starts_at: string | null; ends_at: string | null; created_by_name: string; published_by_name: string | null;
  confirmed_by_name: string | null; is_live: boolean; needs_second_person: boolean;
  seen_count: number; acknowledged_count: number; dismissed_count: number;
  events?: Array<{ id: string; actor_name: string | null; type: string; at: string }>;
};
const STATUS: Record<Status, string> = { draft: 'Draft', pending_confirmation: 'Needs a second admin', published: 'Sent', archived: 'Archived' };
const TONE: Record<Status, PillTone> = { draft: 'neutral', pending_confirmation: 'warn', published: 'ok', archived: 'neutral' };
const DELIVERY: Record<Delivery, string> = { inbox: 'Bell only', banner: 'Bell + banner', popup: 'Bell + sign-in pop-up' };

type Draft = {
  title: string; body: string; link_path: string; delivery: Delivery; tone: Announcement['tone']; everyFirm: boolean;
  firms: string; audience_role: Announcement['audience_role']; release_id: string; starts_at: string; ends_at: string;
};
const EMPTY: Draft = { title: '', body: '', link_path: '', delivery: 'inbox', tone: 'info', everyFirm: true, firms: '', audience_role: 'everyone', release_id: '', starts_at: '', ends_at: '' };

/** datetime-local in the viewer's own time zone ↔ ISO. */
const toLocal = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date(iso).getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '');
const fromLocal = (v: string) => (v ? new Date(v).toISOString() : null);
const draftOf = (a: Announcement): Draft => ({
  title: a.title, body: a.body, link_path: a.link_path ?? '', delivery: a.delivery, tone: a.tone, everyFirm: a.audience_firms === null,
  firms: (a.audience_firms ?? []).join(', '), audience_role: a.audience_role, release_id: a.release_id ?? '',
  starts_at: toLocal(a.starts_at), ends_at: toLocal(a.ends_at),
});
const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function AnnouncementsPage() {
  const { me } = useMe();
  const [items, setItems] = useState<Announcement[] | null>(null);
  const [releases, setReleases] = useState<Array<{ id: string; name: string }>>([]);
  const [editing, setEditing] = useState<Announcement | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, rel] = await Promise.all([
        api.get<{ announcements: Announcement[] }>('/api/admin/announcements'),
        api.get<{ releases: Array<{ id: string; name: string }> }>('/api/admin/releases'),
      ]);
      setItems(list.announcements);
      setReleases(rel.releases);
    } catch (e) { setError(msg(e, 'Could not load announcements.')); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const open = (a: Announcement) => {
    setEditing(a);
    setDraft(draftOf(a));
    api.get<{ announcement: Announcement }>(`/api/admin/announcements/${a.id}`).then((r) => setEditing((c) => (c?.id === a.id ? r.announcement : c)), () => {});
  };
  const locked = Boolean(editing && editing.status !== 'draft');
  const firmList = draft.firms.split(',').map((f) => f.trim()).filter(Boolean);
  const payload = () => {
    const text = { title: draft.title.trim(), body: draft.body.trim(), link_path: draft.link_path.trim() || null, tone: draft.tone, ends_at: fromLocal(draft.ends_at) };
    if (locked) return text;
    return {
      ...text, delivery: draft.delivery, audience_role: draft.audience_role, release_id: draft.release_id || null, starts_at: fromLocal(draft.starts_at),
      audience_firms: draft.everyFirm ? null : firmList,
    };
  };
  const run = async (work: () => Promise<{ announcement: Announcement }>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await work();
      setEditing(r.announcement);
      setDraft(draftOf(r.announcement));
      await load();
    } catch (e) { setError(msg(e, 'That could not be saved.')); } finally { setBusy(false); }
  };
  const save = () => run(() => (editing
    ? api.patch(`/api/admin/announcements/${editing.id}`, payload())
    : api.post('/api/admin/announcements', payload())));
  const act = (verb: 'publish' | 'confirm' | 'archive') => editing && run(() => api.post(`/api/admin/announcements/${editing.id}/${verb}`));
  const iSentIt = Boolean(editing && me && editing.published_by_name === me.name);
  const preview: PreviewAnnouncement = {
    title: draft.title.trim(), body: draft.body.trim(), link_path: draft.link_path.trim() || null, delivery: draft.delivery, tone: draft.tone,
    release_name: releases.find((r) => r.id === draft.release_id)?.name ?? null,
    audience: `${draft.audience_role === 'firm_admins' ? 'Firm admins' : 'Everyone'} at ${draft.everyFirm ? 'every firm' : firmList.length ? firmList.join(', ') : 'the firms you list'}`,
    when: `${draft.starts_at ? `From ${fmtDateTime(fromLocal(draft.starts_at)!)}` : 'From when it’s sent'}${draft.ends_at ? ` to ${fmtDateTime(fromLocal(draft.ends_at)!)}` : ' until archived'}`,
  };

  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h1 className="page-title">Announcements</h1>
        <p className="m-0 text-[13px] text-muted">Messages to firm users in every cell: pinned in their bell, as a banner, or as a pop-up when they sign in.</p>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="flex flex-col gap-3">
          <Button variant="outline" size="sm" className="w-fit" onClick={() => { setEditing(null); setDraft(EMPTY); }}><Megaphone aria-hidden size={15} /> New announcement</Button>
          {!items ? <Spinner label="Loading announcements" /> : items.length === 0 ? <Card className="px-6 py-10 text-center text-muted">No announcements yet.</Card> : (
            <Card className="overflow-hidden p-0">
              <ul className="m-0 list-none divide-y divide-line-soft p-0">
                {items.map((a) => (
                  <li key={a.id}>
                    <button type="button" onClick={() => open(a)} className={cx('flex w-full flex-col gap-1 border-0 bg-transparent px-5 py-4 text-left hover:bg-row-hi', editing?.id === a.id && 'bg-brand-tint')}>
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 flex-1 truncate font-semibold text-ink">{a.title}</span>
                        {a.is_live && <Pill tone="info" dot>Live</Pill>}
                        <Pill tone={TONE[a.status]}>{STATUS[a.status]}</Pill>
                      </span>
                      <span className="text-[13px] text-muted">
                        {DELIVERY[a.delivery]} · {a.audience_firms ? a.audience_firms.join(', ') : 'every firm'}{a.audience_role === 'firm_admins' ? ' · firm admins' : ''}
                        {a.starts_at ? ` · from ${fmtDateTime(a.starts_at)}` : ''}{a.ends_at ? ` to ${fmtDateTime(a.ends_at)}` : ''}
                      </span>
                      {a.status !== 'draft' && (
                        <span className="text-[13px] text-muted">Seen by {a.seen_count}{a.delivery === 'popup' ? ` · ${a.acknowledged_count} said Got it` : ''} · {a.dismissed_count} dismissed</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <Card className="flex flex-col gap-3.5 p-5">
          <h2 className="m-0 text-[15px] font-bold">{editing ? (locked ? 'Sent announcement' : 'Edit draft') : 'New announcement'}</h2>
          {locked && <p className="m-0 text-[13px] text-muted">Once sent, only the text, link, tone and end time can change. To change who gets it, archive it and send a new one.</p>}
          <TextField label="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <TextArea label="Message" rows={5} value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
          <TextField label="Learn more link" optional placeholder="/feedback/mine" hint="A page in the portal." value={draft.link_path} onChange={(e) => setDraft({ ...draft, link_path: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select label="How it arrives" disabled={locked} value={draft.delivery} onChange={(e) => setDraft({ ...draft, delivery: e.target.value as Delivery })}>
              {(Object.keys(DELIVERY) as Delivery[]).map((d) => <option key={d} value={d}>{DELIVERY[d]}</option>)}
            </Select>
            <Select label="Tone" value={draft.tone} onChange={(e) => setDraft({ ...draft, tone: e.target.value as Draft['tone'] })}>
              <option value="info">Information</option>
              <option value="success">Good news</option>
              <option value="warning">Warning</option>
            </Select>
          </div>
          <fieldset disabled={locked} className="m-0 flex flex-col gap-2 border-0 p-0 text-sm">
            <legend className="mb-1 p-0 font-semibold">Who gets it</legend>
            <label className="flex items-center gap-2"><input type="radio" checked={draft.everyFirm} onChange={() => setDraft({ ...draft, everyFirm: true })} /> Every firm</label>
            <label className="flex items-center gap-2"><input type="radio" checked={!draft.everyFirm} onChange={() => setDraft({ ...draft, everyFirm: false })} /> These firms</label>
            {!draft.everyFirm && <TextField label="Firms (cell/firm, comma-separated)" small placeholder="prod-1/default" value={draft.firms} onChange={(e) => setDraft({ ...draft, firms: e.target.value })} />}
            <Select label="People" value={draft.audience_role} onChange={(e) => setDraft({ ...draft, audience_role: e.target.value as Draft['audience_role'] })}>
              <option value="everyone">Everyone at those firms</option>
              <option value="firm_admins">Firm admins only</option>
            </Select>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Starts (your time)" type="datetime-local" disabled={locked} hint="Blank = when sent" value={draft.starts_at} onChange={(e) => setDraft({ ...draft, starts_at: e.target.value })} />
            <TextField label="Ends (your time)" type="datetime-local" hint="Blank = until archived" value={draft.ends_at} onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} />
          </div>
          <Select label={'Release (for "What\'s new")'} disabled={locked} value={draft.release_id} onChange={(e) => setDraft({ ...draft, release_id: e.target.value })}>
            <option value="">None</option>
            {releases.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </Select>
          {draft.delivery === 'popup' && draft.everyFirm && !locked && (
            <Banner tone="warn">A pop-up to every firm needs a second platform admin. After you send it, someone else confirms it before anyone sees it.</Banner>
          )}
          <div className="flex flex-wrap gap-2">
            {(!editing || editing.status !== 'archived') && (
              <Button variant="outline" size="sm" disabled={busy || !draft.title.trim() || !draft.body.trim()} onClick={() => void save()}>{editing ? 'Save' : 'Save draft'}</Button>
            )}
            <Button variant="outline" size="sm" disabled={!draft.title.trim() || !draft.body.trim()} onClick={() => setPreviewing(true)}><Eye aria-hidden size={14} /> Preview</Button>
            {editing?.status === 'draft' && <Button variant="primary" size="sm" disabled={busy} onClick={() => void act('publish')}><Send aria-hidden size={14} /> Send</Button>}
            {editing?.status === 'pending_confirmation' && (
              <Button variant="primary" size="sm" disabled={busy || iSentIt} title={iSentIt ? 'A different platform admin has to confirm it' : undefined} onClick={() => void act('confirm')}>Confirm and send</Button>
            )}
            {editing && editing.status !== 'archived' && <Button variant="danger" size="sm" disabled={busy} onClick={() => void act('archive')}>Archive</Button>}
          </div>
          {editing?.events && editing.events.length > 0 && (
            <details className="text-[13px]">
              <summary className="cursor-pointer font-semibold">Activity</summary>
              <ul className="m-0 mt-2 flex list-none flex-col gap-1 p-0">
                {editing.events.map((e) => <li key={e.id}><span className="font-semibold">{e.actor_name}</span> {e.type.replace(/_/g, ' ')} · {fmtDateTime(e.at)}</li>)}
              </ul>
            </details>
          )}
        </Card>
      </div>
      <AnnouncementPreview open={previewing} onClose={() => setPreviewing(false)} announcement={preview} />
    </div>
  );
}
